import React, { useState, useEffect } from "react";
import { useHistory, RouteComponentProps } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SnackbarProvider } from "notistack";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Typography } from "@mui/material";

import CertificateUpload from "../../components/CertificateUpload/SingleUpload";
import Button from "../../components/Button";
import NavigationBack from "../../components/Navigation";

import useUser from "../../hooks/useUser";
import { fireEvent } from "../../hooks/usePubsub";
import { getCourse } from "../../api/courses";
import { uploadCourseCertificate } from "../../api/certificates";
import type { SubmitPayload } from "../../components/CertificateUpload/useCertificateForm";

interface MatchParams {
  certificateId: string;
  crewId: string;
  userId?: string;
}

interface Course {
  id: number;
  name: string;
  validityPeriodId?: number;
}

const UploadCertificate: React.FC<RouteComponentProps<MatchParams>> = ({ match }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [course, setCourse] = useState<Course | null>(null);
  const { certificateId, crewId: crewIdParam } = match.params;
  const user = useUser();
  const userIdParam = match.params?.userId || user.id;
  const [openWarning, setOpenWarning] = useState(false);
  const [warningText, setWarningText] = useState("");
  const [warningTitle, setWarningTitle] = useState("");

  const warningClose = () => {
    setOpenWarning(false);
    history.goBack();
    fireEvent("pending-changed");
  };

  const userId = Number(userIdParam);
  const crewId = Number(crewIdParam);
  const hasValidIds = !isNaN(userId) && !isNaN(crewId);

  const isCustom = certificateId.startsWith("cc_");
  const courseId = Number(isCustom ? certificateId.replace("cc_", "") : certificateId);

  const submit = async ({ userIds: _userIds, ...values }: SubmitPayload): Promise<string | null> => {
    const result = await uploadCourseCertificate({ ...values, userId, isCustom });
    if (result?.status === "ok") {
      fireEvent("pending-changed");
      setOpenWarning(true);
      setWarningTitle(t("certificate_upload.certificate_under_evaluation"));
      setWarningText(t("certificate_upload.certificate_evaluation_message"));
      return null;
    }
    return result?.reason ?? t("certificate_upload.error_generic");
  };

  useEffect(() => {
    if (!hasValidIds) return;
    (async () => {
      const json = await getCourse(courseId, isCustom);
      setCourse(json);
    })();
  }, [courseId, isCustom, hasValidIds]);

  if (!hasValidIds) {
    return (
      <Typography variant="h6" color="error">
        {t("certificate_upload.invalid_user_crew_id")}
      </Typography>
    );
  }

  return (
    <SnackbarProvider maxSnack={3}>
      <h2>
        <NavigationBack />{" "}
        {isCustom ? t("certificate_upload.custom_certificate_title") : t("certificate_upload.course_title")}
      </h2>
      {course && (
        <CertificateUpload
          crewId={crewId}
          courseId={courseId}
          courseName={course.name}
          isCustom={isCustom}
          validityPeriodMonths={course.validityPeriodId ?? null}
          userId={userId}
          onSubmit={submit}
        />
      )}
      <Dialog
        open={openWarning}
        onClose={warningClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{warningTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{warningText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={warningClose} autoFocus>
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>
    </SnackbarProvider>
  );
};

export default UploadCertificate;
